import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, Eye, EyeOff, Fingerprint, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import API from "@/lib/api";

// In-memory cache for setup check (avoids sessionStorage)
let _setupCache = { val: null, ts: 0 };

export default function Login() {
  const { login, loginWithToken } = useAuth();
  const { branding } = useBranding();
  const [form, setForm] = useState({ email: "", password: "" });
  const [setupForm, setSetupForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [checkingPasskey, setCheckingPasskey] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(null); // null = loading, true/false

  // Check if platform needs first-user setup — cache result for 60s to reduce polling
  useEffect(() => {
    if (_setupCache.val !== null && Date.now() - _setupCache.ts < 60000) {
      setNeedsSetup(_setupCache.val);
      return;
    }
    API.get("/auth/setup-check")
      .then(r => {
        const val = r.data.needs_setup;
        setNeedsSetup(val);
        _setupCache = { val, ts: Date.now() };
      })
      .catch(() => setNeedsSetup(false));
  }, []);

  // Check if passkey is available for this email
  const checkPasskey = useCallback(async (email) => {
    if (!email || !email.includes("@")) { setPasskeyAvailable(false); return; }
    setCheckingPasskey(true);
    try {
      const r = await API.get(`/auth/webauthn/available?email=${encodeURIComponent(email)}`);
      setPasskeyAvailable(r.data.available);
    } catch { setPasskeyAvailable(false); }
    finally { setCheckingPasskey(false); }
  }, []);

  // Debounce passkey check on email change
  useEffect(() => {
    if (needsSetup) return;
    const t = setTimeout(() => checkPasskey(form.email), 500);
    return () => clearTimeout(t);
  }, [form.email, checkPasskey, needsSetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success("Logged in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (setupForm.password !== setupForm.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (setupForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const r = await API.post("/auth/register", {
        name: setupForm.name,
        email: setupForm.email,
        password: setupForm.password,
      });
      const data = r.data;
      if (data.token && data.user) {
        await loginWithToken(data.token, data.user);
        toast.success("Account created — welcome to MEP.Engine");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setSubmitting(true);
    try {
      const optionsRes = await API.post("/auth/webauthn/login/options", { email: form.email });
      const options = optionsRes.data;
      const credential = await startAuthentication({ optionsJSON: options });
      const verifyRes = await API.post("/auth/webauthn/login/verify", credential);
      const data = verifyRes.data;
      if (data.token && data.user) {
        await loginWithToken(data.token, data.user);
        toast.success("Signed in with passkey");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Passkey authentication failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const platformName = branding.platform_name || "MEP";
  const loginTitle = branding.login_title || "Structured MEP pricing for engineering consultancies";

  // Still checking setup status
  if (needsSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="login-page min-h-screen flex flex-col items-center justify-center relative overflow-hidden" data-testid="login-page">
      {/* Atmospheric background */}
      <div className="login-bg absolute inset-0" />
      <div className="login-glow-1 absolute w-[600px] h-[600px] rounded-full" />
      <div className="login-glow-2 absolute w-[400px] h-[400px] rounded-full" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-sm px-6 flex flex-col items-center">
        {/* Logo & Branding */}
        <div className="mb-10 text-center">
          {branding.logo_url ? (
            <div className="logo-pulse-container mx-auto mb-5">
              <img src={branding.logo_url} alt={platformName} className="h-28 mx-auto rounded-2xl" data-testid="login-logo" />
            </div>
          ) : (
            <div className="logo-pulse-container mx-auto mb-5">
              <img src="/furd-logo-dark.png" alt="FURD Tech" className="h-28 rounded-2xl" data-testid="login-logo" />
            </div>
          )}
          <p className="text-sm text-slate-400 tracking-wide leading-relaxed max-w-xs mx-auto" data-testid="login-title">{loginTitle}</p>
        </div>

        {needsSetup ? (
          /* ─── First-User Setup Form ─── */
          <>
            <div className="w-full mb-6 text-center">
              <h2 className="text-lg font-semibold text-white tracking-tight" data-testid="setup-heading">Create Your Account</h2>
              <p className="text-xs text-slate-500 mt-1">First-time setup — you'll be the platform Director.</p>
            </div>
            <form onSubmit={handleSetup} className="w-full space-y-4" data-testid="setup-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="setup-name"
                    type="text"
                    value={setupForm.name}
                    onChange={(e) => setSetupForm({ ...setupForm, name: e.target.value })}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="James Leader"
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="setup-email"
                    type="email"
                    value={setupForm.email}
                    onChange={(e) => setSetupForm({ ...setupForm, email: e.target.value })}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="setup-password"
                    type={showPassword ? "text" : "password"}
                    value={setupForm.password}
                    onChange={(e) => setSetupForm({ ...setupForm, password: e.target.value })}
                    className="login-input h-12 pl-11 pr-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="Min. 6 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="setup-confirm-password"
                    type={showPassword ? "text" : "password"}
                    value={setupForm.confirmPassword}
                    onChange={(e) => setSetupForm({ ...setupForm, confirmPassword: e.target.value })}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="Confirm password"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold h-12 rounded-lg shadow-lg shadow-blue-500/10 transition-all text-sm tracking-wide"
                data-testid="setup-submit-btn"
              >
                {submitting ? "Creating account..." : <>Create Account <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>
          </>
        ) : (
          /* ─── Login Form ─── */
          <>
            <form onSubmit={handleSubmit} className="w-full space-y-5" data-testid="login-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Password</Label>
                  <Link to="/forgot-password" className="text-[11px] text-blue-500 hover:text-blue-400 transition-colors" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="input-password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="login-input h-12 pl-11 pr-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors"
                    tabIndex={-1}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold h-12 rounded-lg shadow-lg shadow-blue-500/10 transition-all text-sm tracking-wide"
                data-testid="login-submit-btn"
              >
                {submitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            {/* Passkey / Biometric Login */}
            {passkeyAvailable && (
              <>
                <div className="flex items-center gap-3 w-full my-4">
                  <div className="flex-1 h-px bg-slate-800" />
                  <span className="text-[10px] text-slate-600 uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-slate-800" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={handlePasskeyLogin}
                  className="w-full border-slate-800 hover:border-slate-700 hover:bg-slate-800/50 text-slate-300 h-12 rounded-lg text-sm font-medium transition-all"
                  data-testid="passkey-login-btn"
                >
                  <Fingerprint className="w-5 h-5 mr-2 text-blue-400" />
                  Sign in with Device
                </Button>
              </>
            )}
          </>
        )}

        {/* Footer info */}
        <div className="mt-8 text-center">
          <p className="text-[11px] text-slate-600">
            {needsSetup ? "This will create your Director account with full admin access." : "Access is by invitation only. Contact your administrator."}
          </p>
        </div>
      </div>
    </div>
  );
}
